import { Dialog, Portal, Button } from '@chakra-ui/react';
import { forwardRef, useState, useRef, useCallback, useImperativeHandle } from 'react';
import i18n from '../utils/i18n';

export type ConfirmModalHandle = {
	open: (props: ConfirmModalProps) => Promise<boolean>;
	close: () => void;
};

type ConfirmModalProps = {
	title: string;
	content: string | React.ReactNode;
	isConfirmButtonVisible?: boolean;
	confirmButtonText?: string;
	confirmButtonVariant?: 'subtle' | 'outline' | 'solid';
	isBackButtonVisible?: boolean;
	backButtonText?: string;
	size?: 'sm' | 'md' | 'lg';
};

export const ConfirmModal = forwardRef<ConfirmModalHandle>((_, ref) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const resolveRef = useRef<((value: boolean) => void) | null>(null);
	const [modalProps, setModalProps] = useState<ConfirmModalProps>({
		title: '',
		content: '',
		size: 'sm',
		isConfirmButtonVisible: true,
		confirmButtonText: '',
		confirmButtonVariant: 'solid',
		isBackButtonVisible: true,
		backButtonText: i18n.t('general.cancel'),
	});

	const open = useCallback((props: ConfirmModalProps) => {
		setIsOpen(true);
		if (props) {
			setModalProps(prev => ({ ...prev, ...props }));
		}
		return new Promise<boolean>(resolve => {
			resolveRef.current = resolve;
		});
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
		resolveRef.current?.(false);
		resolveRef.current = null;
	}, []);

	const confirm = useCallback(() => {
		setIsOpen(false);
		resolveRef.current?.(true);
		resolveRef.current = null;
	}, []);

	useImperativeHandle(ref, () => ({ open, close }), [open, close]);

	return (
		<Dialog.Root
			size={modalProps.size || 'sm'}
			closeOnEscape={true}
			open={isOpen}
			onOpenChange={openStatus => {
				if (openStatus.open === false) {
					close();
				}
			}}
			placement="center"
			closeOnInteractOutside={false}
		>
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner>
					<Dialog.Content>
						<Dialog.Header>
							<Dialog.Title>{modalProps.title}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<Dialog.Body>{modalProps.content}</Dialog.Body>
						{modalProps.isBackButtonVisible || modalProps.isConfirmButtonVisible ? (
							<Dialog.Footer>
								{modalProps.isBackButtonVisible ? (
									<Dialog.ActionTrigger asChild>
										<Button onClick={close} variant="outline">
											{modalProps.backButtonText}
										</Button>
									</Dialog.ActionTrigger>
								) : null}
								{modalProps.isConfirmButtonVisible ? (
									<Button id="confirmButton" variant={modalProps.confirmButtonVariant} onClick={confirm}>
										{modalProps.confirmButtonText}
									</Button>
								) : null}
							</Dialog.Footer>
						) : null}
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
});
